import React, { useState, useEffect } from 'react';
import BlobButton from './BlobButton';
import { X, Clock, Sparkles } from 'lucide-react';
import { submitLeadWithGeo } from '../services/apiService';
import { useGeolocation } from '../hooks/useGeolocation';

const ContactFormModal = ({ isOpen, onClose }) => {
  const { country, isLoading: geoLoading, error: geoError, retry: retryGeo } = useGeolocation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Countdown timer logic
  const SEVEN_DAYS = 7 * 24 * 60 * 60 * 1000;
  const ONE_HOUR = 60 * 60 * 1000;

  const getInitialEndTime = () => {
    const stored = localStorage.getItem('discountEndTime');
    if (stored) {
      return parseInt(stored);
    }
    const endTime = Date.now() + SEVEN_DAYS;
    localStorage.setItem('discountEndTime', endTime.toString());
    return endTime;
  };

  const [endTime, setEndTime] = useState(getInitialEndTime());
  const [timeLeft, setTimeLeft] = useState(endTime - Date.now());

  useEffect(() => {
    const timer = setInterval(() => {
      const remaining = endTime - Date.now();
      
      if (remaining <= ONE_HOUR && remaining > 0) {
        const newEndTime = Date.now() + SEVEN_DAYS;
        setEndTime(newEndTime);
        localStorage.setItem('discountEndTime', newEndTime.toString());
        setTimeLeft(SEVEN_DAYS);
      } else {
        setTimeLeft(remaining);
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [endTime]);

  const formatTime = (ms) => {
    if (ms <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0 };
    
    const days = Math.floor(ms / (24 * 60 * 60 * 1000));
    const hours = Math.floor((ms % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000));
    const minutes = Math.floor((ms % (60 * 60 * 1000)) / (60 * 1000));
    const seconds = Math.floor((ms % (60 * 1000)) / 1000);
    
    return { days, hours, minutes, seconds };
  };

  const time = formatTime(timeLeft);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      // Lock body scroll when modal opens
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      document.addEventListener('keydown', handleEscape);
    } else {
      // Unlock body scroll when modal closes
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    };
  }, [isOpen, onClose]);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone is required';
    } else if (!/^[0-9]{10,}$/.test(formData.phone.replace(/[^0-9]/g, ''))) {
      newErrors.phone = 'Invalid phone number';
    }
    if (!formData.message.trim()) newErrors.message = 'Message is required';
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check geolocation - MANDATORY
    if (geoLoading) {
      alert('Please wait while we detect your location...');
      return;
    }
    
    if (geoError) {
      alert('Unable to detect your location. Please try again.');
      return;
    }
    
    const newErrors = validateForm();
    if (Object.keys(newErrors).length === 0) {
      setIsSubmitting(true);
      try {
        const result = await submitLeadWithGeo({
          fullName: formData.name,
          email: formData.email,
          phoneNumber: formData.phone,
          message: formData.message || 'Modal Form Inquiry'
        });

        if (!result.success) {
          alert(result.message || 'Something went wrong. Please try again.');
        }
        // Success redirect handled by apiService
        onClose();
      } catch (error) {
        console.error('Modal form error:', error);
        alert('Network error. Please try again.');
      } finally {
        setIsSubmitting(false);
      }
    } else {
      setErrors(newErrors);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (errors[e.target.name]) {
      setErrors({ ...errors, [e.target.name]: '' });
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[99999] flex items-center justify-center overflow-hidden">
      {/* Overlay */}
      <div 
        className="absolute inset-0 bg-black bg-opacity-60 backdrop-blur-sm z-[99998]"
        onClick={onClose}
      ></div>

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-5xl w-full mx-4 my-4 z-[99999] max-h-[95vh] overflow-hidden">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-white shadow-lg hover:bg-gray-100 flex items-center justify-center transition-all"
        >
          <X className="w-5 h-5 text-gray-700" />
        </button>

        <div className="grid md:grid-cols-2 h-full">
          {/* Left Side - Image */}
          <div className="hidden md:block relative">
            <img
              src="https://images.unsplash.com/photo-1516979187457-637abb4f9353?crop=entropy&cs=srgb&fm=jpg&q=85&w=800"
              alt="Publishing Services"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-transparent to-white/20"></div>
          </div>

          {/* Right Side - Form */}
          <div className="p-6 md:p-8 lg:p-10 flex flex-col max-h-[95vh]">
            {/* Discount Badge */}
            <div className="flex items-center justify-center gap-2 mb-3">
              <Sparkles className="w-5 h-5 text-amber-700" />
              <span className="text-amber-700 font-bold text-lg">40% OFF First-Time Orders!</span>
            </div>

            {/* Heading */}
            <div className="text-center mb-4">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
                Start Your <span className="text-amber-700">Publishing Journey</span>
              </h2>
              <p className="text-sm text-gray-600">Fill out the form and we'll help you get started</p>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-4 flex-1">
              <div className="relative">
                <input
                  type="text"
                  name="name"
                  placeholder="Your Name"
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full bg-transparent border-0 border-b-2 border-gray-300 focus:border-amber-700 outline-none px-0 py-3 text-gray-900 placeholder:text-gray-500 transition-colors"
                />
                {errors.name && <p className="text-red-500 text-sm mt-1 absolute -bottom-5">{errors.name}</p>}
              </div>

              <div className="relative">
                <input
                  type="email"
                  name="email"
                  placeholder="Email Address"
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full bg-transparent border-0 border-b-2 border-gray-300 focus:border-amber-700 outline-none px-0 py-3 text-gray-900 placeholder:text-gray-500 transition-colors"
                />
                {errors.email && <p className="text-red-500 text-sm mt-1 absolute -bottom-5">{errors.email}</p>}
              </div>

              {/* Country Field - Auto-detected */}
              <div className="relative">
                <input
                  type="text"
                  name="country"
                  placeholder={geoLoading ? "Detecting country..." : "Country"}
                  value={country}
                  disabled
                  className="w-full bg-gray-50 border-0 border-b-2 border-gray-300 outline-none px-0 py-3 text-gray-700 placeholder:text-gray-500 cursor-not-allowed"
                />
              </div>

              <div className="relative">
                <div className="flex gap-3">
                  <select
                    className="bg-transparent border-0 border-b-2 border-gray-300 focus:border-amber-700 outline-none py-3 text-gray-700 transition-colors cursor-pointer"
                    defaultValue="+1"
                  >
                    <option value="+1">🇺🇸 +1</option>
                    <option value="+44">🇬🇧 +44</option>
                    <option value="+91">🇮🇳 +91</option>
                    <option value="+61">🇦🇺 +61</option>
                    <option value="+86">🇨🇳 +86</option>
                  </select>
                  <input
                    type="tel"
                    name="phone"
                    placeholder="Phone Number"
                    value={formData.phone}
                    onChange={handleChange}
                    className="flex-1 bg-transparent border-0 border-b-2 border-gray-300 focus:border-amber-700 outline-none px-0 py-3 text-gray-900 placeholder:text-gray-500 transition-colors"
                  />
                </div>
                {errors.phone && <p className="text-red-500 text-sm mt-1 absolute -bottom-5">{errors.phone}</p>}
              </div>

              <div className="relative">
                <input
                  type="text"
                  name="message"
                  placeholder="Project Details"
                  value={formData.message}
                  onChange={handleChange}
                  className="w-full bg-transparent border-0 border-b-2 border-gray-300 focus:border-amber-700 outline-none px-0 py-3 text-gray-900 placeholder:text-gray-500 transition-colors"
                />
                {errors.message && <p className="text-red-500 text-sm mt-1 absolute -bottom-5">{errors.message}</p>}
              </div>

              {/* Countdown Timer */}
              <div className="flex flex-wrap items-center justify-center gap-2 text-gray-600 py-4 border-t border-gray-200">
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  <span className="text-sm font-semibold">Offer expires in:</span>
                </div>
                
                <div className="flex items-center gap-2">
                  <div className="text-center">
                    <div className="bg-amber-700 rounded px-2 py-1">
                      <span className="text-sm font-bold text-white">{time.days}</span>
                    </div>
                    <span className="text-[10px] text-gray-600 block">Days</span>
                  </div>
                  <div className="text-center">
                    <div className="bg-amber-700 rounded px-2 py-1">
                      <span className="text-sm font-bold text-white">{time.hours}</span>
                    </div>
                    <span className="text-[10px] text-gray-600 block">Hours</span>
                  </div>
                  <div className="text-center">
                    <div className="bg-amber-700 rounded px-2 py-1">
                      <span className="text-sm font-bold text-white">{time.minutes}</span>
                    </div>
                    <span className="text-[10px] text-gray-600 block">Mins</span>
                  </div>
                  <div className="text-center">
                    <div className="bg-amber-700 rounded px-2 py-1">
                      <span className="text-sm font-bold text-white">{time.seconds}</span>
                    </div>
                    <span className="text-[10px] text-gray-600 block">Secs</span>
                  </div>
                </div>
              </div>

              <BlobButton
                type="submit"
                className="w-full"
              >
                Claim 40% OFF & Start Publishing
              </BlobButton>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactFormModal;
